import DOMPurify from 'dompurify';

/**
 * Detecta se a string contém HTML estruturado (evita falso-positivo com "<" solto).
 */
export function isHtmlContent(s: string): boolean {
  return /<(?:html|article|section|div|p|br|table|h[1-6]|ul|ol|li)\b/i.test(s);
}

/**
 * Remove todas as tags HTML e extrai texto puro usando DOMParser do browser.
 * Decodifica entidades naturalmente e colapsa whitespace.
 * Usado nos previews (line-clamp na lista).
 */
export function stripHtmlToText(s: string): string {
  if (!isHtmlContent(s)) return s;

  const doc = new DOMParser().parseFromString(s, 'text/html');
  const text = doc.body.textContent ?? '';
  return text.replace(/\s+/g, ' ').trim();
}

/**
 * Igual a `stripHtmlToText`, mas preserva quebras de linha (parágrafos,
 * <br>, <li>, <div>) em vez de colapsar tudo em uma única linha.
 * Usado na modal de detalhe (com `whitespace-pre-wrap`) para blobs HTML
 * crus de publicações (DJEN), onde a estrutura em linhas ajuda a leitura.
 */
export function stripHtmlToTextPreserveBreaks(s: string): string {
  if (!isHtmlContent(s)) return s;

  const doc = new DOMParser().parseFromString(s, 'text/html');
  doc.querySelectorAll('style, script, head').forEach((el) => el.remove());
  doc.querySelectorAll('br').forEach((el) => el.replaceWith('\n'));
  doc.querySelectorAll('p, div, li, tr, h1, h2, h3, h4, h5, h6').forEach((el) => {
    el.append('\n');
  });

  const text = doc.body.textContent ?? '';
  return text
    .replace(/[ \t]+/g, ' ')
    .replace(/\n[ \t]+/g, '\n')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/**
 * Detecta blob de documento HTML/XML cru (ex: publicacao do DJEN colada
 * direto na descricao de uma task) — marcadores que nao aparecem em HTML
 * legitimo de usuario (mention-chip, links de comentario etc).
 */
export function looksLikeHtmlDocument(s: string): boolean {
  return /<\?xml|<!doctype|<html|<head|<body|<style|<meta|\salign=/i.test(s);
}

/**
 * Sanitiza HTML de fonte externa (tribunal/CNJ) via DOMPurify.
 * Mantém formatação visual básica; remove scripts, estilos, iframes e
 * atributos de comportamento (on*, data-*, contentEditable).
 */
export function sanitizeDjenHtml(s: string): string {
  return DOMPurify.sanitize(s, {
    ALLOWED_TAGS: [
      'p', 'br', 'span', 'div', 'section', 'article',
      'b', 'strong', 'i', 'em', 'u', 's', 'sub', 'sup',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li',
      'table', 'thead', 'tbody', 'tr', 'td', 'th',
      'a', 'blockquote',
    ],
    ALLOWED_ATTR: ['align', 'colspan', 'rowspan', 'href', 'target', 'rel'],
    FORBID_TAGS: ['style', 'script', 'head', 'meta', 'link', 'iframe', 'title'],
    FORBID_ATTR: ['contenteditable', 'onerror', 'onload', 'onclick'],
    ALLOW_DATA_ATTR: false,
  });
}
